import "../../utils/document.scss";

const TERMS = "http://www.thetotemchat.com/help/terms";
const PRIVACY = "http://www.thetotemchat.com/help/privacy";
const GUIDELINES = "https://thetotemchat.com/help/community-guidelines";

const Terms = () => {
  return (
    <main className="document">
      <div className="document__content">
        <section className="document__section">
          <h2 className="document__section-title">
            Totem Chat Terms of Service
          </h2>
          <p className="document__section-paragraph">
            Thanks for reading. These Totem Chat Terms of Service (the “Terms”)
            are a legal contract between you and Totem Chat, Inc. (“Totem Chat,”
            “we” or “us”). You represent that you have the power and authority
            to agree to these Terms. These Terms explain how you are permitted
            to use the Totem Chat mobile application and services, as well as
            the Totem Chat website located at{" "}
            <a className="document__link" href="http://www.thetotemchat.com/">
              thetotemchat.com
            </a>{" "}
            (the “Site”) and any content therein (collectively, the “Services”).
            Unless otherwise specified, all references to the Services include
            the services available through the Totem Chat mobile application
            (the “App”) and the Site, as well as any software that Totem Chat
            provides to you that allows you to access the Services from a mobile
            device and any other Materials (as defined below). By browsing,
            accessing, linking, streaming, viewing, using or downloading
            information from our Services, you agree to these Terms, our{" "}
            <a className="document__link" href={PRIVACY}>
              Privacy Policy
            </a>
            , and any additional terms and conditions that may apply to specific
            sections of the Services. If you do not agree with any of these
            Terms, do not access or otherwise use the Services. If you are using
            the Services on behalf of another person or are a parent or guardian
            of the minor user of the Services, you represent that you are
            authorized to accept these Terms on that person’s behalf. You may
            download and/or print a copy of these Terms for your records at:{" "}
            <a className="document__link" href={TERMS}>
              thetotemchat.com/terms
            </a>
            .
          </p>
          <p className="document__section-paragraph document__bold-text">
            Important Note: These Terms contain a dispute resolution and
            arbitration provision, including class action waiver, which affects
            your rights under these Terms and with respect to any dispute you
            may have with Totem Chat. You may opt out of the binding individual
            arbitration and class action waiver as provided below.
          </p>
        </section>

        <section className="document__section">
          <h2 className="document__section-title">
            Changes to the Terms and the Services
          </h2>
          <p className="document__section-paragraph">
            Totem Chat may make changes, update, or add or remove provisions to
            these Terms at any time by posting the updated Terms on applicable
            Services, or by notifying you via the App or any other method. By
            using the Services after Totem Chat has updated the Terms, you are
            agreeing to all of the updated Terms; if you do not agree with any
            of the updated Terms, you must stop using the Services.
          </p>
          <p className="document__section-paragraph">
            In addition, we may, for any or no reason and at our discretion,
            modify, disable, or discontinue operation of the Services or your
            access to the Services without notice and without liability to you
            or any other party. Reasons for termination may include provision of
            false or misleading registration information, interference with
            other users or the administration of our services, or violation of
            these Terms or any of our other policies or agreements. Upon denial
            of access, you agree not to attempt to access the Services.
            Termination will not limit any of our other rights or remedies. We
            also may limit your use of the Services, including, for example,
            limits on the amount of content you upload or the number of posts
            you make and your frequency of access.
          </p>
        </section>

        <section className="document__section">
          <h2 className="document__section-title">General Use</h2>
          <p className="document__section-paragraph">
            The Services may not be available in all locations, and we may block
            access to the Services from certain locations based on your device’s
            geolocation information. We may add or remove the areas in which the
            Services are not available at any time, without notice to you.
          </p>
          <p className="document__section-paragraph">
            By using the Services, you represent, acknowledge and agree that you
            are at least 13 years of age. If you are not at least 13 years old,
            you may not use the Services at any time or in any manner or submit
            any information to the App, the Site or any part of the Services. If
            you are between the ages of 13 and 18, you may only use the Services
            with the approval of a parent or guardian.
          </p>
          <p className="document__section-paragraph">
            We may, in our sole discretion, refuse to offer the Services to any
            person or entity. We may, without notice and in our sole discretion,
            terminate your right to use the Services, or any portion thereof,
            and block or prevent your future access to and use of the Services
            or any portion thereof.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Your User Account</h2>
          <p className="document__section-paragraph">
            In order to use our App, you will be required to create a user
            account. You are responsible for using the settings available to
            protect the confidentiality of your personal information. You are
            responsible for all uses of your account, whether or not you have
            authorized them. You agree to provide, maintain and update true,
            accurate, current, and complete information about yourself in the
            registration process. You may not impersonate any person or entity
            or misrepresent your identity or affiliation with any person or
            entity, including using another person’s account information. You
            agree to promptly notify us of unauthorized use of your account
            information or any other breach of security that you suspect or
            become aware of involving or relating to the Services. Our Services
            may offer you the opportunity to share content on third-party
            applications or websites. We do not control such services and are
            not responsible for them. We fully disclaim all liability associated
            with them.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">
            Third-Party Distribution Channels
          </h2>
          <p className="document__section-paragraph">
            Totem Chat offers software applications that may be made available
            through the Apple App Store, the Google Play Store or other
            distribution channels (“Distribution Channels”). If you obtain such
            software through a Distribution Channel, you may be subject to
            additional terms of the Distribution Channel. These Terms of Service
            are between you and us only, and not with the Distribution Channel.
            To the extent that you utilize any other third-party products and
            services in connection with your use of our Services, you agree to
            comply with all applicable terms of any agreement for such
            third-party products and services.
          </p>
          <p className="document__section-paragraph">
            With respect to Software that is made available for your use in
            connection with an Apple®-branded product (such software,
            “Apple-Enabled Software”), in addition to the other terms and
            conditions set forth in these Terms of Service, the following terms
            and conditions apply:
          </p>
          <p className="document__section-paragraph">
            You acknowledge that these Terms are between you and Totem Chat
            only, and not with Apple, Inc. (“Apple”), and that as between Totem
            Chat and Apple, Totem Chat, not Apple, is solely responsible for the
            Apple-Enabled Software and the content thereof.
          </p>
          <p className="document__section-paragraph">
            Your use of the Apple-Enabled Software must comply with Apple’s
            then-current App Store Terms of Service.
          </p>
          <p className="document__section-paragraph">
            You may not use the Apple-Enabled Software in any manner that is in
            violation of or inconsistent with the Usage Rules set forth for
            Apple-Enabled Software in, or otherwise be in conflict with, the App
            Store Terms of Service.
          </p>
          <p className="document__section-paragraph">
            You acknowledge that Apple has no obligation to provide maintenance
            and support services with respect to the Apple-Enabled Software. To
            the maximum extent permitted by applicable law, Apple will have no
            warranty obligation whatsoever with respect to the Apple-Enabled
            Software.
          </p>
          <p className="document__section-paragraph">
            You agree that Totem Chat, and not Apple, is responsible for
            addressing any claims by you or any third party relating to the
            Apple-Enabled Software or your possession and/or use of that
            Apple-Enabled Software.
          </p>
          <p className="document__section-paragraph">
            You agree that Totem Chat, and not Apple, shall be solely
            responsible, to the extent required by these Terms, for the
            investigation, defense, settlement and discharge of any third-party
            intellectual property infringement claim in the event that the
            Apple-Enabled Software or the end-user’s possession and use of that
            Apple-Enabled Software infringes that third party’s intellectual
            property rights.
          </p>
          <p className="document__section-paragraph">
            You represent and warrant that (i) you are not located in a country
            that is subject to a U.S. Government embargo, or that has been
            designated by the U.S. Government as a “terrorist supporting”
            country; and (ii) you are not listed on any U.S. Government list of
            prohibited or restricted parties.
          </p>
          <p className="document__section-paragraph">
            You agree to comply with all applicable third-party terms of
            agreement when using the Apple-Enabled Software (e.g., you must not
            be in violation of your wireless data service terms of agreement
            when using the Apple-Enabled Software).
          </p>
          <p className="document__section-paragraph">
            You and we agree that Apple and Apple’s subsidiaries are third-party
            beneficiaries to these Terms with respect to the Apple-Enabled
            Software. Upon your acceptance of these Terms, Apple will have the
            right (and will be deemed to have accepted the right) to enforce
            these Terms against you with respect to the Apple-Enabled Software
            as a third-party beneficiary thereof.
          </p>
          <p className="document__section-paragraph">
            If you breach any of these Terms, the above license will terminate
            automatically, and you must immediately stop using the Services and
            destroy any downloaded or printed Materials.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Mobile Services</h2>
          <p className="document__section-paragraph">
            The Services include certain services, including the App and the
            Site, that are available via a mobile device, including (i) the
            ability to upload content to the Service via a mobile device, (ii)
            the ability to browse the Service from a mobile device and (iii) the
            ability to access features through an application downloaded and
            installed on a mobile device (collectively, the “Mobile Services”).
            To the extent you access the Service through a mobile device, your
            wireless service carrier’s standard charges, data rates and other
            fees may apply. In addition, downloading, installing, or using
            certain Mobile Services may be prohibited or restricted by your
            carrier, and not all Mobile Services may work with all carriers or
            devices. By using the Mobile Services, you agree that we may
            communicate with you regarding Totem Chat and other entities by SMS,
            MMS, text message or other electronic means to your mobile device
            and that certain information about your usage of the Mobile Services
            may be communicated to us. In the event you change or deactivate
            your mobile telephone number, you agree to promptly update your
            Totem Chat account information to ensure that your messages are not
            sent to the person that acquires your old number.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Privacy</h2>
          <p className="document__section-paragraph">
            We respect your privacy and will handle your personal information
            pursuant to our{" "}
            <a className="document__link" href={PRIVACY}>
              Privacy Policy
            </a>
            . By using our Services, you consent to the collection and use of
            personal information submitted by you in accordance with our Privacy
            Policy. You acknowledge that transmission of information by the
            Internet is never completely secure. Except as otherwise stated in
            our Privacy Policy, we do not guarantee the security of any
            information transmitted to or from our Services, including to or
            from any external sites linked to our Services, and submission of
            any information to our Services or to any external sites linked to
            our Services is your risk and responsibility. Without limiting the
            foregoing, you expressly acknowledge that any Submissions (defined
            below) you submit to the Services may be viewable by all other users
            of the Services and any third party.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Submissions</h2>
          <p className="document__section-paragraph">
            You are responsible for any information, opinions, messages,
            comments, and other content or material that you submit, upload,
            post or otherwise make available on, through or in connection with
            the Services (each a “Submission”). When you provide Submissions,
            you agree that those Submissions shall not be in violation of the
            “Safety” section below or the Community Guardrails. Although Totem
            Chat may, in its sole discretion, monitor, screen, modify, refuse,
            remove or edit Submissions for any reason, Totem Chat is not
            obligated to do so. You have full responsibility for each Submission
            you make, including its legality, reliability and appropriateness,
            and will be solely liable for any damage or harm resulting from your
            Submissions.
          </p>
          <p className="document__section-paragraph">
            Subject to our Privacy Policy, we may retain your Submissions, even
            after they have expired from view within the Services or even after
            you have deleted them. However, you agree that we have no obligation
            to retain any Submission for any period of time, nor shall we be
            responsible or liable to you or any third party for any lost content
            or losses related to the expiration or deletion of a Submission.
          </p>
          <p className="document__section-paragraph">
            Unless otherwise explicitly stated herein or in the Privacy Policy
            (including personally identifying information, the treatment of
            which is described in the Privacy Policy), you agree that any
            Submission provided by you in connection with the Services is
            provided on a non-proprietary and non-confidential basis. You hereby
            grant to Totem Chat a non-exclusive, perpetual, irrevocable,
            royalty-free, fully paid-up, worldwide license (including the right
            to sublicense through multiple tiers) to use, reproduce, process,
            adapt, publicly perform, publicly display, modify, prepare
            derivative works, publish, transmit and distribute each of your
            Submissions, or any portion thereof, in any form, medium or
            distribution method now known or hereafter existing, known or
            developed, and authorize others to do the same (“Submission
            License”). Without limiting the foregoing, you agree that the
            Submission License gives Totem Chat the right to sublicense
            Submissions to third parties in connection with the syndication,
            broadcast, distribution, promotion, or publication of Submissions in
            any and all media or distribution methods, now known or later
            developed. No use of Submissions in accordance with the Submission
            License shall entitle you to any compensation from Totem Chat, or
            any third party. You agree to pay for all royalties, fees, damages
            and any other monies owing any person by reason of any Submissions
            posted by you to or through the Services.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Safety</h2>
          <p className="document__section-paragraph">
            At Totem Chat, we value your safety and try hard to keep our
            Services a safe place for all users. But we can’t guarantee it.
            That’s where you come in. By using the Services, you agree that:
          </p>
          <p className="document__section-paragraph">
            You will not violate any law, contract, intellectual property or
            other third-party right or unlawfully injure another person or their
            property, and that you are solely responsible for your conduct while
            on the Services and you use the Services at your own risk.
          </p>
          <p className="document__section-paragraph">
            You will not post, store, create or otherwise publish or send
            through the Services any User Content that violates our{" "}
            <a href={GUIDELINES}>Community Guidelines</a>, which we may update
            from time to time.
          </p>
          <p className="document__section-paragraph">
            You will not impersonate another person in a manner that is intended
            to or does mislead, confuse or deceive others.
          </p>
          <p className="document__section-paragraph">
            You will not post or share another individual’s private information
            without their express authorization and permission.
          </p>
          <p className="document__section-paragraph">
            You will not attempt to indicate that you have a relationship with
            Totem Chat or that Totem Chat has endorsed you or any products or
            services.
          </p>
          <p className="document__section-paragraph">
            You will not defame, abuse, bully, harass, stalk, threaten, or
            otherwise violate the legal rights of others.
          </p>
          <p className="document__section-paragraph">
            You will not use racially or ethnically offensive language.
          </p>
          <p className="document__section-paragraph">
            You will not discuss or incite illegal or violent activity.
          </p>
          <p className="document__section-paragraph">
            You will not post or share Submissions that contain pornography or
            graphic violence.
          </p>
          <p className="document__section-paragraph">
            You will not post or share Submissions that exploit children or
            minors or that depict cruelty to animals.
          </p>
          <p className="document__section-paragraph">
            You will not post or share Submissions that violate any third-party
            right, including any copyright, trademark, patent, trade secret,
            moral right, privacy right, right of publicity or any other
            intellectual property or proprietary right.
          </p>
          <p className="document__section-paragraph">
            You will not disseminate any unsolicited or unauthorized
            advertising, promotional materials, junk mail, spam, chain letters,
            pyramid schemes, or any other form of such solicitation.
          </p>
          <p className="document__section-paragraph">
            You will not use any robot, spider, crawler, scraper or other
            automated means to access the Services.
          </p>
          <p className="document__section-paragraph">
            You will not take any action that imposes an unreasonable or
            disproportionately large load on our infrastructure.
          </p>
          <p className="document__section-paragraph">
            You will not use or develop any third-party applications that
            interact with the Services or Submissions without our prior written
            consent.
          </p>
          <p className="document__section-paragraph">
            You will not alter the opinions or comments posted by others on the
            Services.
          </p>
          <p className="document__section-paragraph">
            You will not post or share any image or language that is obscene,
            vulgar or offensive or that threatens, disparages or demeans any
            individual or group.
          </p>
          <p className="document__section-paragraph">
            You will not post or share any image or language that promotes
            discrimination based on race, religion, sex, nationality,
            disability, sexual orientation or age.
          </p>
          <p className="document__section-paragraph">
            You will not post or share any image or language that violates the
            legal rights (including the rights of publicity and privacy) of
            others or contains any material that could give rise to any civil or
            criminal liability under applicable laws or regulations or that
            otherwise may be in conflict with these Terms and our Privacy
            Policy.
          </p>
          <p className="document__section-paragraph">
            You will not attempt to circumvent any of our content-filtering
            techniques.
          </p>
          <p className="document__section-paragraph">
            You will not post or share anything inappropriate or disruptive to
            the Services.
          </p>
          <p className="document__section-paragraph">
            You will not disrupt, negatively affect or inhibit users from having
            a positive experience with the Services.
          </p>
          <p className="document__section-paragraph">
            You will not use the Services in violation of these Terms and/or for
            any unlawful purposes.
          </p>
          <p className="document__section-paragraph">
            You will not post or share anything contrary to our public image,
            goodwill or reputation.
          </p>
          <p className="document__section-paragraph">
            This list of prohibitions provides examples and is not exhaustive or
            exclusive. Totem Chat reserves the right to (a) suspend or terminate
            access and your ability to post to the Services (or otherwise use
            the Services), (b) delete, remove or refuse to distribute any
            Submissions, all with or without cause and with or without notice,
            for any reason or no reason without liability to you. If Totem Chat
            believes a Submission violates these Terms, it may make that
            Submission invisible to other users without notifying you. Your
            Submission will be visible to you,but will not appear for any other
            user. Totem Chat may report to law enforcement authorities any
            actions that may be illegal, and any reports it receives of such
            conduct. When legally required or at Totem Chat’s discretion, Totem
            Chat will cooperate with law enforcement agencies in any
            investigation of alleged illegal activity on the Services or on the
            Internet. To the extent the law allows, we reserve the right to
            reveal your identity or any other information we know about you if a
            complaint or legal action arises from your Submission.
          </p>
          <p className="document__section-paragraph">
            Unauthorized use of any Materials or Third-Party Content contained
            in the Services may violate certain laws and regulations. You agree
            to indemnify and hold us and our parents, subsidiaries, and
            affiliates, and our and their respective officers, directors,
            members, shareholders, employees, contractors, consultants, agents,
            representatives, licensors, suppliers, service providers, successors
            and assigns (collectively, the “Indemnified Entities”) harmless from
            and against any and all costs, damages, liabilities, and expenses
            (including attorneys’ fees and costs of defense) Totem Chat or any
            other Indemnified Entity suffers in relation to, arising from, or
            for the purpose of avoiding, any claim or demand from a third party
            that your use of the Services or the use of the Services by any
            person using your phone number or email account to register and/or
            your password violates any applicable law or regulation, or the
            copyrights, trademark rights or other rights of any third party.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">
            Public Forums and Interactive Communications
          </h2>
          <p className="document__section-paragraph document__bold-text">
            Our Services offer public forums and other interactive
            communications. Please be aware that these features are public or
            may be subject to public access. You acknowledge that public forums
            offered on the Services are for public and not private
            communications. Please use respect when you interact with others
            through our Services. You are responsible for the content you post
            and the consequences of posting the content and your interactions
            with third parties through our Services. Such dealings are solely
            between you and such third parties. We urge you to be cautious about
            these interactions. Your use or reliance on such content is at your
            own risk, and we are not responsible for it. We reserve the right to
            remove without notice any posting or communication for any reason,
            but we have no obligation to delete content that you may find
            objectionable or offensive. Information and opinions expressed in
            public forums or other interactive communications by others are not
            necessarily ours.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">
            Third-Party Content, Links and Information
          </h2>
          <p className="document__section-paragraph">
            Our Services may include content posted or uploaded by third parties
            (“Third-Party Content”). Unless our Services make clear otherwise,
            we do not claim ownership of Third-Party Content and it does not
            necessarily express our views. Because it is not content we have
            posted or uploaded, we are not responsible for Third-Party Content
            and cannot verify its accuracy, timeliness, completeness or
            usefulness. We have the right, but not the obligation, to monitor,
            edit or delete Third-Party Content. Your use or reliance on
            Third-Party Content is at your own risk, and we are not responsible
            for it.
          </p>
          <p className="document__section-paragraph">
            If you have a dispute with any other user of our Services or any
            third party, you agree that the dispute is between you and the third
            party and we are under no obligation to become involved, and you
            release us and our parents, subsidiaries, affiliates, members,
            officers, directors, shareholders, employees, consultants,
            affiliates, agents, representatives, service providers, licensors,
            successors, assigns, licensors, and business partners from claims
            demands, and damages of every kind or nature arising out of,
            relating to, or in any way connected to such dispute.
          </p>
          <p className="document__section-paragraph">
            Our Services may use third-party trademarks and logos to identify
            third parties and resources. Our use of these marks does not imply
            affiliation with or endorsement of the third party. These marks are
            the property of their respective owners.
          </p>
          <p className="document__section-paragraph">
            You may experience links to third-party materials or other access to
            other sites that are not owned, controlled or even in relationship
            with Totem Chat. Totem Chat has no control over such sites and
            resources and Totem Chat is not responsible for and does not endorse
            such sites and resources. You further acknowledge and agree that
            Totem Chat will not be responsible or liable, directly or
            indirectly, for any damage or loss caused or alleged to be caused by
            or in connection with use of or reliance on any content, events,
            goods or services available on or through any such site or resource.
            Totem Chat has no liability for any loss or claim for use of any
            third-party website, service, or content while using the Service. If
            an advertiser were to promote a service or good on Totem Chat, your
            relationship is solely between you and the advertiser. Totem Chat is
            not responsible for any loss or damages from a relationship with the
            advertiser.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Service Interruptions</h2>
          <p className="document__section-paragraph">
            We will do our best to provide uninterrupted access to our Services,
            but we do not guarantee continuous and uninterrupted access to our
            Services, and operation of our Services may be affected by
            circumstances outside of our control. You are responsible for
            providing or obtaining Internet access and any hardware or software
            necessary to connect to the Internet or use our Site.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Mobile Access</h2>
          <p className="document__section-paragraph">
            Your access or use of our Services through a mobile device will
            require wireless service and may require software. Our Services may
            offer text-messaging capability. You are responsible for these
            requirements, all applicable charges and fees incurred from them,
            and the terms of your agreements with your mobile service and
            telecommunications providers. Not all features of our Services may
            work with or be permitted by your device or network provider. We are
            not responsible for the unavailability or delays in wireless,
            text-messaging, or telecommunications service, or any loss, damage
            or security breach or disclosure of your information to third
            parties from use of such services.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Force Majeure</h2>
          <p className="document__section-paragraph">
            If you or we are prevented from performing or are unable to perform
            any obligation under these Terms due to any cause beyond the
            reasonable control of the party invoking this provision, the
            affected party’s performance will be extended for the period of
            delay or inability to perform due to such occurrence, but in no
            event later than 60 days.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Not Professional Advice</h2>
          <p className="document__section-paragraph">
            The content on our Services is provided for general informational
            and entertainment purposes only and should not be construed as
            advice. We do not warrant the accuracy, completeness or usefulness
            or this information. The content is not intended to, and does not,
            constitute spiritual, legal, medicinal, financial or professional
            advice, and may not be used for such purposes. Any reliance you
            place on such information is strictly at your own risk.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Disclaimer of Warranties</h2>
          <p className="document__section-paragraph">
            YOU UNDERSTAND AND AGREE THAT USE OF OUR SERVICES IS AT YOUR SOLE
            RISK, ANY MATERIAL OR DATA DOWNLOADED OR OTHERWISE OBTAINED THROUGH
            THE USE OF OUR SERVICES IS AT YOUR OWN DISCRETION AND RISK AND YOU
            WILL BE SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOUR COMPUTER SYSTEM OR
            LOSS OF DATA THAT RESULTS FROM THE DOWNLOAD OF SUCH MATERIAL OR
            DATA. OUR SERVICES AND THE MATERIALS ARE PROVIDED ON AN “AS IS” AND
            “AS AVAILABLE” BASIS. THE MATERIALS MAY NOT HAVE BEEN VERIFIED OR
            AUTHENTICATED IN WHOLE OR IN PART BY YIK YAK, AND THEY MAY INCLUDE
            INACCURACIES OR TYPOGRAPHICAL OR OTHER ERRORS. YIK YAK DOES NOT
            WARRANT THE ACCURACY, COMPLETENESS OR TIMELINESS OF THE SERVICES OR
            THE MATERIALS. YIK YAK HAS NO LIABILITY FOR ANY LOSS OF, OR ERRORS
            OR OMISSIONS IN SUBMISSIONS, OR FOR ANY ERRORS OR OMISSIONS IN THE
            MATERIALS OR ANY OTHER PORTION OF THE SERVICES, WHETHER PROVIDED BY
            YIK YAK, OUR LICENSORS OR SUPPLIERS OR OTHER USERS. TO THE FULLEST
            EXTENT PERMITTED BY APPLICABLE LAW, WE MAKE NO REPRESENTATIONS OR
            WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, REGARDING THE USE OR THE
            RESULTS OF OUR SERVICES IN TERMS OF CORRECTNESS, ACCURACY,
            RELIABILITY, OR OTHERWISE. WE ARE NOT LIABLE FOR ANY INTERRUPTIONS,
            ERRORS, COMPUTER VIRUSES, SPYWARE, OR OTHER HARMFUL COMPONENTS IN
            THE USE OF OUR SERVICES. WE DISCLAIM ALL EXPRESS OR IMPLIED
            WARRANTIES WITH REGARD TO THE SERVICES AND THE MATERIALS, INCLUDING
            THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A
            PARTICULAR PURPOSE, NON-INFRINGEMENT, TITLE, FREEDOM FROM DEFECTS,
            UNINTERRUPTED USE, AND ALL WARRANTIES IMPLIED FROM ANY COURSE OF
            DEALING OR USAGE OF TRADE. THIS INCLUDES LOSS OF DATA OR PROFIT
            ARISING OUT OF THE USE OR THE INABILITY TO USE THE SERVICES. WE DO
            NOT WARRANT THAT ANY CONTENT OR INFORMATION ACCESSED THROUGH OUR
            SERVICES WILL BE UNINTERRUPTED OR ERROR FREE, DEFECTS WILL BE
            CORRECTED, OR OUR SITE OR THE SERVERS THAT MAKE OUR SITE AVAILABLE
            ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Limitation of Liability</h2>
          <p className="document__section-paragraph">
            IN NO EVENT WILL WE OR ANY OF OUR PARENTS, SUBSIDIARIES, OR
            AFFILIATES, OR THEIR OR OUR RESPECTIVE OFFICERS, DIRECTORS, MEMBERS,
            SHAREHOLDERS, EMPLOYEES, CONTRACTORS, CONSULTANTS, AGENTS,
            REPRESENTATIVES, LICENSORS, SUPPLIERS, SERVICE PROVIDERS, SUCCESSORS
            OR ASSIGNS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DAMAGES OF
            ANY KIND, INCLUDING DIRECT, INDIRECT, SPECIAL, CONSEQUENTIAL,
            INCIDENTAL, EXEMPLARY OR PUNITIVE DAMAGES, OR DAMAGES FOR LOST
            PROFITS, LOSS OF REVENUE, OR LOSS OF USE, ARISING OUT OF OR RELATED
            TO ANY USE OF OUR SERVICES OR THE INFORMATION CONTAINED ON OUR
            SERVICES, THE USE OF OR THE INABILITY TO USE OUR SERVICES, OR ANY
            GOODS OR SERVICES PURCHASED OR OBTAINED OR MESSAGES RECEIVED OR
            TRANSACTIONS ENTERED INTO THROUGH OUR SERVICES, OR LOSS OF,
            UNAUTHORIZED ACCESS TO OR ALTERATION OF A USER’S TRANSMISSIONS OR
            DATA OR FOR THE COST OF PROCUREMENT OF SUBSTITUTE GOODS AND
            SERVICES, WHETHER SUCH DAMAGES ARISE IN CONTRACT, NEGLIGENCE, TORT,
            STRICT LIABILITY, UNDER STATUTE, IN EQUITY, AT LAW, OR OTHERWISE,
            EVEN IF WE OR OUR REPRESENTATIVES HAVE BEEN ADVISED OF THE
            POSSIBILITY OF SUCH DAMAGES. IN NO EVENT WILL OUR TOTAL LIABILITY TO
            YOU FOR ALL DAMAGES, LOSSES, OR CAUSES OF ACTION EXCEED THE GREATER
            OF THE TOTAL FEES PAID BY YOU FOR THE SERVICES GIVING RISE TO THE
            CAUSE OF ACTION OR ONE TENGE (₸1.00). SOME JURISDICTIONS DO NOT
            ALLOW FOR THE LIMITATION OR EXCLUSION OF LIABILITY FOR INCIDENTAL OR
            CONSEQUENTIAL DAMAGES. IN THOSE JURISDICTIONS, OUR LIABILITY IS
            LIMITED TO THE GREATEST EXTENT PERMITTED BY LAW.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Advertising</h2>
          <p className="document__section-paragraph">
            The Services may contain advertisements. In consideration for Totem
            Chat letting you access and use the Services, you agree that,
            subject to our Privacy Policy, we, our affiliates, and our
            third-party partners may place advertising on the Services. Because
            the Services contain content that you and other users provide to us,
            advertising may sometimes appear near your content.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Feedback</h2>
          <p className="document__section-paragraph">
            If you send or transmit any communications, comments, questions,
            suggestions, or related materials regarding Totem Chat or the
            Services, whether by email or otherwise (collectively, “Feedback”),
            such Feedback is, and will be treated as, non-confidential and
            non-proprietary. You hereby assign all right, title, and interest
            in, and Totem Chat is free to use, without any attribution or
            compensation to you, any and all Feedback for any purpose
            whatsoever. You understand and agree that Totem Chat is not
            obligated to use, display, reproduce, or distribute any such ideas,
            know-how, concepts, or techniques contained in the Feedback, and you
            have no right to compel such use, display, reproduction, or
            distribution.
          </p>
          <p className="document__section-paragraph">
            If you offer any comments or suggestions about the Service, you
            agree that your disclosure is gratuitous, was not requested and
            without restriction and will not place Totem Chat under any
            fiduciary or other obligation, and that Totem Chat has the ability
            to use your suggestion for other purposes, and/or to share the idea
            to anyone without confidentiality. If Totem Chat were to receive
            your suggestion or idea, Totem Chat still has rights to ideas
            similar to your suggestion or conceived from sources besides you.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Taxes</h2>
          <p className="document__section-paragraph">
            You are responsible for all taxes, duties, levies, and fees,
            including any sales, use or withholding taxes, imposed on or in
            connection with use of our Services by any taxing authority
            worldwide
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Relationship of Parties</h2>
          <p className="document__section-paragraph">
            You acknowledge that no joint venture, partnership, employment, or
            agency relationship exists between you and us as a result of these
            Terms or any use of the Services. You agree not to hold yourself out
            as our representative, agent, or employee, and we shall not be
            liable for any representation, act or omission made by you.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Indemnification</h2>
          <p className="document__section-paragraph">
            You agree to defend, indemnify and hold harmless us, our parents,
            subsidiaries, and affiliates, and their and our respective officers,
            directors, members, shareholders, employees, contractors,
            consultants, agents, representatives, licensors, suppliers, service
            providers, successors and assigns from and against any claims,
            liabilities, damages, judgments, awards, losses, costs, expenses or
            fees (including reasonable attorneys’ fees) arising out of or
            relating to your violation of these Terms or your representations
            and warranties in these Terms, your use of the Services, including,
            without limitation, your Submissions, any use of the Site’s or App’s
            content, services and products other than as expressly authorized in
            these Terms or your use of any information obtained from the Site or
            App, or infringed by you, or other use of the Site or App using your
            computer, of any intellectual property or any other right or any
            person or entity. You will cooperate as fully as reasonably required
            in the defense of any claim. We reserve the right, at our expense,
            to assume the exclusive defense and control of any matter otherwise
            subject to indemnification by you, and you will not in any event
            settle any such matter without our written consent.
          </p>
        </section>
        <section className="document__section">
          <h2 className="document__section-title">Contact Us</h2>
          <p className="document__section-paragraph">
            If you have any questions about these Terms or otherwise need to
            contact Totem Chat for any reason, you can reach us at:
          </p>
          <p className="document__section-paragraph">Totem Chat, Inc.</p>
          <p className="document__section-paragraph">hello@thetotemchat.com</p>
        </section>
      </div>
    </main>
  );
};

export default Terms;
